import {
  ArrowUpRight,
  CircleCheck,
  FileText,
  Hand,
  ListChecks,
  MailCheck,
  PackageCheck,
  PackageOpen,
  PhoneCall,
  Send,
  ShoppingBag,
  TriangleAlert,
  Truck,
  UserPlus,
  Wrench,
} from "lucide-react";
import { useCanAccess } from "ra-core";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

import type { GulaOrder } from "../gulaTypes";
import {
  describeOrderEvent,
  eventTime,
  orderEvents,
  type HomeKpis,
  type OrderEventKind,
} from "./homeKpis";

type Tone = "normal" | "action" | "alert";

/** A square: big number, label, and a line with the detail that matters. */
const Square = ({
  icon: Icon,
  value,
  label,
  detail,
  tone = "normal",
  link,
  onClick,
  step,
}: {
  icon: typeof UserPlus;
  value: number;
  label: string;
  detail?: React.ReactNode;
  tone?: Tone;
  link?: string;
  onClick?: () => void;
  step?: number;
}) => {
  const className = cn(
    "group flex min-h-36 flex-col gap-2 rounded-3xl border p-4 text-left transition-colors",
    tone === "action"
      ? "border-lime-300 bg-lime-200 text-neutral-900 hover:bg-lime-300"
      : tone === "alert"
        ? "border-red-300 bg-red-50 text-red-950 hover:bg-red-100 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100 dark:hover:bg-red-500/15"
        : "bg-card hover:bg-muted/50",
  );
  const content = (
    <>
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full",
            tone === "action"
              ? "bg-neutral-900 text-lime-300"
              : tone === "alert"
                ? "bg-red-600 text-white"
                : "bg-muted text-muted-foreground",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        {step ? (
          <span
            className={cn(
              "text-[11px] font-medium uppercase tracking-wide",
              tone === "normal" ? "text-muted-foreground" : "opacity-70",
            )}
          >
            Paso {step}
          </span>
        ) : (
          <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-60" />
        )}
      </div>
      <div className="text-3xl font-semibold tabular-nums leading-none">
        {value}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        {detail ? (
          <span
            className={cn(
              "text-xs",
              tone === "normal" ? "text-muted-foreground" : "opacity-80",
            )}
          >
            {detail}
          </span>
        ) : null}
      </div>
    </>
  );
  return link ? (
    <Link to={link} className={className}>
      {content}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
};

/** "Hoy en Gula": the numbers that matter today, sales and shipping. */
export const HomeKpiPanel = ({
  kpis,
  slot,
  onShowTasks,
  showOperations = true,
}: {
  kpis: HomeKpis;
  slot: string | null;
  onShowTasks: () => void;
  /** Shipping and installation tiles (not for setters) */
  showOperations?: boolean;
}) => {
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "money",
    action: "list",
  });
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Ventas
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <Square
            icon={UserPlus}
            value={kpis.leadsToday}
            label="Leads hoy"
            detail={`Ayer: ${kpis.leadsYesterday}`}
            link="/clientes"
          />
          <Square
            icon={Hand}
            value={kpis.unowned}
            label="Sin coger"
            detail={kpis.unowned ? kpis.unownedNames : "Todos tienen dueño"}
            tone={kpis.unowned ? "action" : "normal"}
            link="/clientes?agente=none"
          />
          <Square
            icon={PhoneCall}
            value={kpis.callNow}
            label="Quieren llamada ahora"
            detail={
              slot
                ? `Franja ${slot} h · sin llamar hoy`
                : "Fuera de horario de llamadas"
            }
            tone={kpis.callNow ? "action" : "normal"}
            link="/clientes"
          />
          <Square
            icon={ListChecks}
            value={kpis.myTasksToday}
            label="Mis tareas hoy"
            detail={
              kpis.myTasksOverdue
                ? `+${kpis.myTasksOverdue} vencidas`
                : "Ninguna vencida"
            }
            tone={kpis.myTasksOverdue > 0 ? "alert" : "normal"}
            onClick={onShowTasks}
          />
          <Square
            icon={ShoppingBag}
            value={kpis.salesMonth}
            label="Ventas este mes"
            detail={`Mes pasado: ${kpis.salesLastMonth}`}
            link={isAdmin ? "/dinero" : "/clientes"}
          />
        </div>
      </div>

      {showOperations ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Envíos e instalaciones
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            <Square
              step={1}
              icon={PackageOpen}
              value={kpis.toShip}
              label="Por enviar"
              detail={
                kpis.toShip
                  ? kpis.toShipLate
                    ? `${kpis.toShipLate} lleva${kpis.toShipLate === 1 ? "" : "n"} más de 2 días`
                    : kpis.toShipNames
                  : "Nada pendiente"
              }
              tone={
                kpis.toShipLate ? "alert" : kpis.toShip ? "action" : "normal"
              }
              link="/operaciones"
            />
            <Square
              step={2}
              icon={Truck}
              value={kpis.inTransit}
              label="En camino"
              detail={
                kpis.incidents
                  ? `${kpis.incidents} con incidencia`
                  : kpis.inTransit
                    ? `Seguimiento al cliente: ${kpis.inTransitWithTracking}/${kpis.inTransit}${kpis.outForDelivery ? ` · ${kpis.outForDelivery} en reparto hoy` : ""}`
                    : "Ninguno en ruta"
              }
              tone={
                kpis.incidents || kpis.inTransitWithTracking < kpis.inTransit
                  ? "alert"
                  : "normal"
              }
              link="/operaciones"
            />
            <Square
              step={3}
              icon={PackageCheck}
              value={kpis.received}
              label="Recibidos por el cliente"
              detail={
                kpis.receivedWithoutParte
                  ? `${kpis.receivedWithoutParte} sin parte al instalador`
                  : kpis.receivedToday
                    ? `${kpis.receivedToday} hoy`
                    : kpis.received
                      ? kpis.receivedNames
                      : "Ninguno esperando"
              }
              tone={kpis.receivedWithoutParte ? "action" : "normal"}
              link="/operaciones"
            />
            <Square
              step={4}
              icon={FileText}
              value={kpis.installerNotified}
              label="Parte enviado al instalador"
              detail={
                kpis.installerNotified
                  ? `${kpis.installationsDated} con fecha de instalación${kpis.installerNotifiedToday ? ` · ${kpis.installerNotifiedToday} hoy` : ""}`
                  : "Ninguna instalación pendiente"
              }
              tone={
                kpis.installerNotified > kpis.installationsDated
                  ? "action"
                  : "normal"
              }
              link="/operaciones"
            />
            <Square
              step={5}
              icon={Wrench}
              value={kpis.installedMonth}
              label="Instalados este mes"
              detail="Clientes con el agua funcionando"
              link="/operaciones"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
};

const EVENT_ICON: Record<
  OrderEventKind,
  { icon: typeof Truck; className: string }
> = {
  shipped: { icon: Send, className: "text-sky-600 dark:text-sky-400" },
  tracking: { icon: MailCheck, className: "text-sky-600 dark:text-sky-400" },
  out_for_delivery: {
    icon: Truck,
    className: "text-amber-600 dark:text-amber-400",
  },
  incident: { icon: TriangleAlert, className: "text-destructive" },
  received: {
    icon: PackageCheck,
    className: "text-lime-600 dark:text-lime-400",
  },
  parte: { icon: FileText, className: "text-violet-600 dark:text-violet-400" },
  installed: {
    icon: Wrench,
    className: "text-emerald-600 dark:text-emerald-400",
  },
};

/** What happened to the orders lately: shipped, tracking sent, received... */
export const ShippingFeed = ({
  orders,
  now = new Date(),
}: {
  orders: GulaOrder[];
  now?: Date;
}) => {
  const recent = orderEvents(orders, new Date(now.getTime() - 2 * 86_400_000));
  const events = recent.length
    ? recent
    : orderEvents(orders, new Date(now.getTime() - 7 * 86_400_000));
  return (
    <section className="flex h-full flex-col gap-3 rounded-3xl border bg-card p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Truck className="h-5 w-5" />
          Movimientos de envíos
        </h2>
        <span className="text-xs text-muted-foreground">
          {recent.length ? "Últimas 48 h" : "Últimos 7 días"}
        </span>
      </div>
      {events.length ? (
        <ul className="flex flex-col divide-y">
          {events.slice(0, 8).map((event) => {
            const { title, detail } = describeOrderEvent(event);
            const style = EVENT_ICON[event.kind];
            const Icon = style.icon;
            return (
              <li key={event.key}>
                <Link
                  to={`/clientes?cliente=${event.order.client_id}`}
                  className="flex items-start gap-3 py-2.5 hover:opacity-80"
                >
                  <Icon
                    className={cn("mt-0.5 h-4 w-4 shrink-0", style.className)}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">
                      {title}
                    </span>
                    {detail ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {detail}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {eventTime(event.at, now)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <CircleCheck className="h-4 w-4" />
          Sin movimientos esta semana.
        </p>
      )}
      <Link
        to="/operaciones"
        className="mt-auto text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
      >
        Ver Operaciones →
      </Link>
    </section>
  );
};
